// @systeme: primitives-ecran
// ============================================================================
// PairsForm — ConfirmDialog / SaisieDialog : confirmer un geste, demander un
// texte (lot 7.4)
// ============================================================================
// Les deux dialogs que `ConfirmProvider` (hooks/useConfirm) monte une seule
// fois pour toute l'app, à la place des 88 `window.confirm` et 8
// `window.prompt` natifs (non stylés, bloquants, illisibles sur mobile,
// invisibles aux tests). Un écran ne les rend jamais lui-même : il appelle
// `confirmer(…)` / `demanderTexte(…)`.
//   - ConfirmDialog : petit, centré même sur mobile (ce n'est pas un
//     formulaire), bouton de confirmation « error » quand le geste détruit ;
//   - SaisieDialog : un champ (texte, multiligne ou date), plein écran sur
//     mobile via DialogMobile, Entrée valide, vide refusé si `obligatoire`.
// Les messages hérités de window.confirm contiennent des retours à la ligne :
// rendus tels quels (`pre-line`).
// ============================================================================

import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import {
  Button, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField,
} from "@mui/material";
import { DialogMobile } from "./DialogMobile";

export interface OptionsConfirmation {
  titre?: string;
  message: ReactNode;
  /** Libellé du bouton qui confirme (« Confirmer »). */
  libelleConfirmer?: string;
  libelleAnnuler?: string;
  /** Geste destructeur : bouton de confirmation rouge. */
  danger?: boolean;
}

export interface OptionsSaisie {
  titre?: string;
  message?: ReactNode;
  label?: string;
  valeurInitiale?: string;
  /** Une saisie vide (ou blanche) ne peut pas être validée. */
  obligatoire?: boolean;
  multiligne?: boolean;
  type?: "text" | "date";
  libelleValider?: string;
  libelleAnnuler?: string;
}

interface PropsConfirmDialog {
  ouvert: boolean;
  options: OptionsConfirmation | null;
  onReponse: (confirme: boolean) => void;
}

export function ConfirmDialog({ ouvert, options, onReponse }: PropsConfirmDialog) {
  const o = options ?? { message: "" };
  return (
    <DialogMobile
      open={ouvert}
      onClose={() => onReponse(false)}
      fullScreen={false}
      maxWidth="xs"
      aria-labelledby="confirm-dialog-titre"
    >
      <DialogTitle id="confirm-dialog-titre">{o.titre ?? "Confirmer"}</DialogTitle>
      <DialogContent>
        {typeof o.message === "string" ? (
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>{o.message}</DialogContentText>
        ) : (
          o.message
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onReponse(false)}>{o.libelleAnnuler ?? "Annuler"}</Button>
        <Button
          onClick={() => onReponse(true)}
          variant="contained"
          color={o.danger ? "error" : "primary"}
          autoFocus
        >
          {o.libelleConfirmer ?? "Confirmer"}
        </Button>
      </DialogActions>
    </DialogMobile>
  );
}

interface PropsSaisieDialog {
  ouvert: boolean;
  options: OptionsSaisie | null;
  /** `null` = annulé (même contrat que window.prompt). */
  onReponse: (texte: string | null) => void;
}

export function SaisieDialog({ ouvert, options, onReponse }: PropsSaisieDialog) {
  const o = options ?? {};
  const [texte, setTexte] = useState(o.valeurInitiale ?? "");
  useEffect(() => {
    if (ouvert) setTexte(o.valeurInitiale ?? "");
  }, [ouvert, o.valeurInitiale]);
  const invalide = Boolean(o.obligatoire) && texte.trim() === "";

  const valider = (e?: FormEvent) => {
    e?.preventDefault();
    if (invalide) return;
    onReponse(texte);
  };

  return (
    <DialogMobile open={ouvert} onClose={() => onReponse(null)} maxWidth="sm" aria-labelledby="saisie-dialog-titre">
      <form onSubmit={valider}>
        <DialogTitle id="saisie-dialog-titre">{o.titre ?? "Saisie"}</DialogTitle>
        <DialogContent>
          {o.message !== undefined && o.message !== "" && (
            <DialogContentText sx={{ whiteSpace: "pre-line", mb: 2 }}>{o.message}</DialogContentText>
          )}
          <TextField
            autoFocus
            fullWidth
            label={o.label}
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            multiline={Boolean(o.multiligne)}
            minRows={o.multiligne ? 3 : undefined}
            type={o.type ?? "text"}
            slotProps={{
              inputLabel: o.type === "date" ? { shrink: true } : undefined,
              htmlInput: { "aria-label": o.label ?? "Saisie" },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onReponse(null)}>{o.libelleAnnuler ?? "Annuler"}</Button>
          <Button type="submit" variant="contained" disabled={invalide}>
            {o.libelleValider ?? "Valider"}
          </Button>
        </DialogActions>
      </form>
    </DialogMobile>
  );
}
